import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import type { ScenePlan, EnvironmentView, TypedSseEnvelope } from "@pushback-and-chaos/contracts";
import { apiClient } from "./transport.js";
import { toInPlayData, toThread, type InPlayData, type StreamDerived, type ThreadEntry } from "./adapt.js";
import { classifyError, EnvelopeLedger, runTurn, type ConnectionState, type TurnFailure } from "./turn-machine.js";

/**
 * The in-play session: server state, the live envelope tail, and the turn
 * lifecycle. Everything the play surface needs and nothing it computes
 * itself.
 */

export interface LiveDecorations {
  /** Diegetic NPC tells (`NPC_STATE_CHANGED.tell`), newest last. */
  readonly npcTells: readonly string[];
  /** Diegetic status text (`STATUS_CHANGED.status`), newest last. */
  readonly statuses: readonly string[];
  /**
   * Deliberately absent: PRESSURE_CHANGED. Its payload is a bare
   * `SituationPressure` enum and apps/api attaches no authored diegetic text
   * to it (docs/DIAGNOSIS-golden-transcript.md finding e, unresolved). An
   * undecorated enum on screen is worse than silence, so the client renders
   * nothing for it. The envelope is still counted in the dev panel's raw log.
   */
  readonly suppressedPressureChanges: number;
}

export interface SessionModel {
  readonly data: InPlayData | null;
  /**
   * The server-derived environment scene (ADR 0009): the latest
   * ENVIRONMENT_CHANGED envelope wins, falling back to the state fetch.
   * The client draws it and derives nothing.
   */
  readonly environment: EnvironmentView | null;
  /**
   * The turn's transient ScenePlan (ADR 0014), from the latest
   * ENVIRONMENT_CHANGED envelope: played once over the section, never
   * derived here, null when nothing moved or after a resume.
   */
  readonly environmentPlan: ScenePlan | null;
  /**
   * The story thread (fix-phase 3a): every turn's learner input and what
   * came back, fetched from GET /sessions/:id/turns — server state, not
   * client reconstruction, so it survives reload and resume.
   */
  readonly thread: readonly ThreadEntry[];
  /** The input currently being processed, echoed optimistically at the end of the thread. */
  readonly pendingText: string | null;
  readonly decorations: LiveDecorations;
  readonly connection: ConnectionState;
  readonly resumedFromTurn: number | null;
  readonly busy: boolean;
  readonly failure: TurnFailure | null;
  readonly isLoading: boolean;
  readonly envelopeLog: readonly TypedSseEnvelope[];
  readonly turnCount: number;
  readonly submit: (text: string) => Promise<void>;
  readonly retry: () => Promise<void>;
  readonly dismissFailure: () => void;
}

interface StreamState extends StreamDerived {
  readonly environment: EnvironmentView | null;
  readonly environmentPlan: ScenePlan | null;
  readonly npcTells: readonly string[];
  readonly statuses: readonly string[];
  readonly suppressedPressureChanges: number;
  readonly lastEventId: number;
}

const EMPTY_STREAM: StreamState = {
  beat: null,
  environment: null,
  environmentPlan: null,
  openThreads: [],
  npcTells: [],
  statuses: [],
  suppressedPressureChanges: 0,
  lastEventId: 0,
};

function applyEnvelope(state: StreamState, envelope: TypedSseEnvelope): StreamState {
  const lastEventId = Math.max(state.lastEventId, envelope.id);
  switch (envelope.type) {
    case "BEAT":
      return { ...state, lastEventId, beat: { id: envelope.data.beatId, title: envelope.data.title } };
    case "THREAD_OPENED":
      return { ...state, lastEventId, openThreads: [...state.openThreads, envelope.data.summary] };
    case "NPC_STATE_CHANGED":
      return { ...state, lastEventId, npcTells: [...state.npcTells, envelope.data.tell] };
    case "STATUS_CHANGED":
      return { ...state, lastEventId, statuses: [...state.statuses, envelope.data.status] };
    case "PRESSURE_CHANGED":
      return { ...state, lastEventId, suppressedPressureChanges: state.suppressedPressureChanges + 1 };
    case "ENVIRONMENT_CHANGED":
      return { ...state, lastEventId, environment: envelope.data.view, environmentPlan: envelope.data.plan };
    default:
      return { ...state, lastEventId };
  }
}

export function useSession(sessionId: string | null, onTurnComplete?: () => void): SessionModel {
  const client = apiClient();
  const queryClient = useQueryClient();

  const [stream, setStream] = useState<StreamState>(EMPTY_STREAM);
  const [envelopeLog, setEnvelopeLog] = useState<readonly TypedSseEnvelope[]>([]);
  const [connection, setConnection] = useState<ConnectionState>("live");
  const [resumedFromTurn, setResumedFromTurn] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<TurnFailure | null>(null);
  const [turnCount, setTurnCount] = useState(0);

  const ledger = useRef(new EnvelopeLedger());
  const pending = useRef<{ text: string; key: string } | null>(null);

  const query = useQuery({
    queryKey: ["session", sessionId],
    enabled: sessionId !== null,
    queryFn: () => client.getSession(sessionId as string),
    // Never a silent background refetch mid-turn: a half-applied state swap
    // under a learner's cursor is worse than slightly stale narration.
    refetchOnWindowFocus: false,
    retry: 1,
  });

  const turnsQuery = useQuery({
    queryKey: ["session-turns", sessionId],
    enabled: sessionId !== null,
    queryFn: () => client.getSessionTurns(sessionId as string),
    refetchOnWindowFocus: false,
    retry: 1,
  });

  const ingest = useCallback((envelope: TypedSseEnvelope) => {
    if (!ledger.current.admit(envelope)) return;
    setEnvelopeLog((log) => [...log, envelope]);
    setStream((s) => applyEnvelope(s, envelope));
  }, []);

  const runOnce = useCallback(
    async (text: string, key: string) => {
      if (sessionId === null) return;
      setBusy(true);
      setFailure(null);
      setConnection("live");

      const result = await runTurn({ client, sessionId, learnerText: text, idempotencyKey: key, onEnvelope: ingest });

      if (result.ok) {
        pending.current = null;
        setTurnCount((n) => n + 1);
        await queryClient.invalidateQueries({ queryKey: ["session", sessionId] });
        await queryClient.invalidateQueries({ queryKey: ["session-turns", sessionId] });
        setBusy(false);
        onTurnComplete?.();
        return;
      }

      const f = result.failure ?? classifyError(new Error("unknown"));
      setFailure(f);
      setConnection(f.kind === "network" || f.kind === "timeout" ? "reconnecting" : "live");

      // A dropped or timed-out turn may still have completed server-side.
      // Resume the durable tail from the last envelope we actually applied
      // rather than assuming either outcome, and say so on screen.
      if (f.kind === "network" || f.kind === "timeout") {
        const resumed = await resumeTail(sessionId, stream.lastEventId, ingest, ledger.current);
        if (resumed.recovered) {
          setConnection("live");
          setResumedFromTurn(resumed.throughEventId);
          setFailure(null);
          pending.current = null;
          setTurnCount((n) => n + 1);
          await queryClient.invalidateQueries({ queryKey: ["session", sessionId] });
          await queryClient.invalidateQueries({ queryKey: ["session-turns", sessionId] });
          setBusy(false);
          onTurnComplete?.();
          return;
        }
        setConnection("lost");
      }

      if (f.refetch) {
        await queryClient.invalidateQueries({ queryKey: ["session", sessionId] });
        await queryClient.invalidateQueries({ queryKey: ["session-turns", sessionId] });
      }
      setBusy(false);
      onTurnComplete?.();
    },
    // `stream.lastEventId` is read inside; including the whole stream object
    // would rebuild this callback on every envelope.
    [client, sessionId, ingest, queryClient, stream.lastEventId, onTurnComplete],
  );

  const submit = useCallback(
    async (text: string) => {
      const key = globalThis.crypto.randomUUID();
      pending.current = { text, key };
      await runOnce(text, key);
    },
    [runOnce],
  );

  /**
   * Retry reuses the SAME idempotency key. apps/api replays the stored
   * response for a repeated key without a second roll, so a retry after an
   * ambiguous failure is safe by construction rather than by the learner
   * guessing whether the first attempt landed.
   */
  const retry = useCallback(async () => {
    const p = pending.current;
    if (p === null) return;
    await runOnce(p.text, p.key);
  }, [runOnce]);

  const dismissFailure = useCallback(() => setFailure(null), []);

  // Clear per-session state when the session changes.
  useEffect(() => {
    ledger.current = new EnvelopeLedger();
    pending.current = null;
    setStream(EMPTY_STREAM);
    setEnvelopeLog([]);
    setConnection("live");
    setResumedFromTurn(null);
    setFailure(null);
    setTurnCount(0);
  }, [sessionId]);

  const data = useMemo(
    () => (query.data === undefined ? null : toInPlayData(query.data, stream)),
    [query.data, stream],
  );

  const thread = useMemo(() => toThread(turnsQuery.data?.turns ?? []), [turnsQuery.data]);

  return {
    data,
    environment: stream.environment ?? query.data?.environment ?? null,
    environmentPlan: stream.environmentPlan,
    thread,
    pendingText: busy ? (pending.current?.text ?? null) : null,
    decorations: {
      npcTells: stream.npcTells,
      statuses: stream.statuses,
      suppressedPressureChanges: stream.suppressedPressureChanges,
    },
    connection,
    resumedFromTurn,
    busy,
    failure,
    isLoading: query.isLoading,
    envelopeLog,
    turnCount,
    submit,
    retry,
    dismissFailure,
  };
}

const RESUME_ATTEMPTS = 4;

/**
 * Reads the durable SSE tail from `afterId` with backoff. Bounded, and it
 * stops at the first quiet moment rather than holding the stream open — the
 * stream endpoint never ends on its own (it heartbeats), so an unbounded
 * drain here would hang the recovery path forever.
 */
async function resumeTail(
  sessionId: string,
  afterId: number,
  onEnvelope: (e: TypedSseEnvelope) => void,
  ledger: EnvelopeLedger,
): Promise<{ recovered: boolean; throughEventId: number }> {
  const client = apiClient();
  let through = afterId;

  for (let attempt = 0; attempt < RESUME_ATTEMPTS; attempt += 1) {
    const controller = new AbortController();
    const quiet = setTimeout(() => controller.abort(), 4_000);
    let got = 0;
    try {
      ledger.rewind();
      for await (const envelope of client.streamSession(sessionId, {
        lastEventId: afterId,
        signal: controller.signal,
        maxReconnectAttempts: 0,
      })) {
        if (envelope.type === "HEARTBEAT") break;
        got += 1;
        through = Math.max(through, envelope.id);
        onEnvelope(envelope);
      }
    } catch {
      /* fall through to the backoff below */
    } finally {
      clearTimeout(quiet);
      controller.abort();
    }
    if (got > 0) return { recovered: true, throughEventId: through };
    await new Promise((r) => setTimeout(r, 400 * 2 ** attempt));
  }
  return { recovered: false, throughEventId: through };
}
